/**
 * `TechnicalInstant` — an absolute point in time, stored `timestamptz` in UTC.
 * ADR-006 §2 and §5.
 *
 * The other half of the pair. A business date is the same date everywhere; an
 * instant is the same moment everywhere and only becomes a wall clock when a
 * zone is named. DT-3 forbids the two from converting implicitly, so every
 * crossing in this file takes an explicit zone, and the business-date direction
 * takes an explicit time of day as well.
 *
 * The underlying representation is epoch milliseconds rather than a `Date`,
 * because a `Date` is exactly the type people reach for when they want the
 * other concept.
 */
import { businessDateOf, toDayNumber, partsOf } from './business-date.js';
/** TZ-1: the default display zone. TZ-2: the IANA name, never a fixed offset. */
export const DEFAULT_DISPLAY_ZONE = 'Asia/Tehran';
const MS_PER_DAY = 86_400_000;
export function instantFromEpochMs(ms) {
    if (!Number.isFinite(ms))
        throw new TypeError(`instant expects a finite epoch, got ${ms}`);
    return ms;
}
export const epochMsOf = (instant) => instant;
/**
 * The adapter at the driver boundary — `timestamptz` arrives from `pg` as a
 * `Date`, and this is where it stops being one.
 */
export function instantFromDate(date) {
    const ms = date.getTime();
    if (Number.isNaN(ms))
        throw new TypeError('instantFromDate received an invalid Date');
    return instantFromEpochMs(ms);
}
/**
 * The explicit escape for third-party APIs that take a `Date`. ADR-006 §10
 * names this as the residual risk and asks that it live in one reviewed place
 * rather than at each call site.
 */
export const toDate = (instant) => new Date(instant);
export const toISOString = (instant) => new Date(instant).toISOString();
export function instantFromISOString(text) {
    // Deliberately strict: an ISO-8601 instant carries an offset or a Z. A bare
    // `2026-08-14T10:00:00` is a wall clock with no zone, which is neither of
    // this package's two types, and parsing it would silently pick one.
    if (!/^-?\d{4,6}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/.test(text)) {
        throw new TypeError(`not an absolute instant: ${JSON.stringify(text)}. An offset or Z is required — a ` +
            'timestamp without one is a wall clock, and choosing a zone for it here is exactly the ' +
            'implicit conversion DT-3 forbids.');
    }
    const ms = Date.parse(text);
    if (Number.isNaN(ms))
        throw new TypeError(`unparseable instant: ${JSON.stringify(text)}`);
    return instantFromEpochMs(ms);
}
const partsFormatters = new Map();
function partsFormatter(zone) {
    let formatter = partsFormatters.get(zone);
    if (formatter === undefined) {
        formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: zone,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            era: 'short',
        });
        partsFormatters.set(zone, formatter);
    }
    return formatter;
}
/** The wall clock an instant shows in a zone. */
export function zonedPartsOf(instant, zone) {
    const parts = partsFormatter(zone).formatToParts(new Date(instant));
    const read = (type) => {
        const found = parts.find((part) => part.type === type);
        if (found === undefined)
            throw new Error(`Intl did not return a ${type} for zone ${zone}`);
        return found.value;
    };
    // BC years come back as positive with era 'BC'; the sign has to be restored
    // or a pre-1 AD instant would render as its mirror image.
    const era = parts.find((part) => part.type === 'era')?.value;
    const year = Number(read('year')) * (era === 'BC' || era === 'B' ? -1 : 1);
    return {
        year,
        month: Number(read('month')),
        day: Number(read('day')),
        hour: Number(read('hour')),
        minute: Number(read('minute')),
        second: Number(read('second')),
    };
}
/**
 * TZ-3. Turning an instant into a date is a zone-dependent operation, so it
 * goes through here and takes the zone as an argument. It is emphatically not
 * `toISOString().slice(0, 10)`, which silently answers the question for UTC.
 */
export function businessDateInZone(instant, zone = DEFAULT_DISPLAY_ZONE) {
    const { year, month, day } = zonedPartsOf(instant, zone);
    return businessDateOf(year, month, day);
}
/**
 * The zone's offset from UTC, in milliseconds, at a given instant.
 *
 * The instant is floored to its second before subtracting, because `Intl` only
 * reports the wall clock down to the second. Subtracting a millisecond-precise
 * instant from a second-precise wall clock returns the offset minus the
 * instant's own millisecond remainder — so `…T00:00:00.123Z` in Tehran would
 * report 12,599,877 ms instead of 12,600,000. Flooring (rather than rounding)
 * keeps it correct for negative epochs, where the second containing an instant
 * runs toward negative infinity.
 */
export function zoneOffsetMs(instant, zone) {
    const parts = zonedPartsOf(instant, zone);
    return wallClockMs(parts) - Math.floor(instant / 1000) * 1000;
}
function wallClockMs(parts) {
    const days = toDayNumber(businessDateOf(parts.year, parts.month, parts.day));
    return days * MS_PER_DAY + parts.hour * 3_600_000 + parts.minute * 60_000 + parts.second * 1_000;
}
/**
 * DT-3: `BusinessDate -> TechnicalInstant` requires naming a zone **and** a
 * time of day, explicitly, at the call site. There is no default and no helper
 * that quietly supplies one, because "midnight in whose zone" is precisely the
 * question that gets answered wrong.
 *
 * ## Wall clocks that do not exist, or exist twice
 *
 * A named wall clock is not always one instant. On a spring-forward the hour
 * is skipped and the wall clock never happens; on a fall-back it happens
 * twice. The rule here matches `Temporal`'s `compatible` disambiguation:
 *
 * - **gap** — the result is shifted *forward* past the transition, so
 *   requesting a skipped 02:30 returns the instant that reads 03:30;
 * - **overlap** — the **earlier** of the two occurrences.
 *
 * Shifting forward rather than throwing is a deliberate choice, and Tehran is
 * why. Iran's pre-2022 spring-forward ran 23:59:59 → 01:00, so **midnight
 * itself did not exist** on the first day of DST — which makes `{ hour: 0 }`,
 * the single most common call this function gets, the gap case for every one
 * of those dates. Throwing would refuse start-of-day queries over historical
 * Iranian data. Returning 01:00 answers them.
 *
 * The naive two-pass resolution gets this wrong in a way that is worse than
 * either: for a skipped 02:30 it returns the instant that reads **01:30** — an
 * hour *before* what was asked, colliding with a genuine 01:30 request. That
 * is silent, and it moves a recorded event backwards across a boundary.
 */
export function instantAt(date, options) {
    const { year, month, day } = partsOf(date);
    const { hour, minute = 0, second = 0 } = options.time;
    if (hour < 0 || hour > 23)
        throw new RangeError(`hour ${hour} is not 0-23`);
    if (minute < 0 || minute > 59)
        throw new RangeError(`minute ${minute} is not 0-59`);
    if (second < 0 || second > 59)
        throw new RangeError(`second ${second} is not 0-59`);
    const { zone } = options;
    const wall = wallClockMs({ year, month, day, hour, minute, second });
    // The offsets a day either side. Transitions are never closer together than
    // that, so these are the only two candidates the wall clock can belong to.
    const before = zoneOffsetMs(instantFromEpochMs(wall - MS_PER_DAY), zone);
    const after = zoneOffsetMs(instantFromEpochMs(wall + MS_PER_DAY), zone);
    if (before === after)
        return instantFromEpochMs(wall - before);
    // A candidate is real only if the zone actually reports that offset at it.
    const early = wall - before;
    const late = wall - after;
    const earlyIsReal = zoneOffsetMs(instantFromEpochMs(early), zone) === before;
    const lateIsReal = zoneOffsetMs(instantFromEpochMs(late), zone) === after;
    if (earlyIsReal && lateIsReal)
        return instantFromEpochMs(Math.min(early, late)); // overlap
    if (earlyIsReal)
        return instantFromEpochMs(early);
    if (lateIsReal)
        return instantFromEpochMs(late);
    // Neither is real: the wall clock is in the gap. `early` is the wall time
    // read with the pre-transition offset, which lands after the transition.
    return instantFromEpochMs(early);
}
/**
 * Rendering an instant for display. TZ-1 supplies the default zone, TZ-2 keeps
 * it an IANA name so the zone database resolves historical offsets rather than
 * a hardcoded `+03:30` corrupting every pre-2022 audit row.
 */
export function renderInstant(instant, options = {}) {
    const { zone = DEFAULT_DISPLAY_ZONE, locale = 'en-US', format } = options;
    // The type forbids it; this catches the JavaScript caller and the `as any`.
    // Ignoring it silently would be as wrong as honouring it: an instant asked
    // for in Tehran and rendered in UTC is a day out for three and a half hours
    // of every day.
    if (format !== undefined && 'timeZone' in format) {
        throw new TypeError('renderInstant: format.timeZone is not accepted — the zone is the `zone` argument, which is ' +
            'authoritative. Passing both would make which one wins a matter of spread order.');
    }
    // `dateStyle`/`timeStyle` and the individual component options are mutually
    // exclusive in Intl — mixing them is a TypeError, not a silent preference.
    // So a caller who names any component gets exactly what they named, and only
    // a caller who names none gets the default pair.
    const resolved = format === undefined || Object.keys(format).length === 0
        ? { dateStyle: 'medium', timeStyle: 'medium' }
        : format;
    return new Intl.DateTimeFormat(locale, {
        hourCycle: 'h23',
        ...resolved,
        // Last, so it cannot be overridden by anything above.
        timeZone: zone,
    }).format(new Date(instant));
}
//# sourceMappingURL=instant.js.map